export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(amount);
};

export const formatDate = (dateString) => {
  // Handle null, undefined, or empty string - return today's date
  if (!dateString || dateString === null || dateString === undefined || dateString === '') {
    const today = new Date();
    return today.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
  
  const date = new Date(dateString);
  
  // Check if date is valid - return today's date if invalid
  if (isNaN(date.getTime()) || date.getFullYear() < 1970) {
    const today = new Date();
    return today.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
  
  return date.toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const formatDateOnly = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

export const formatTime = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const formatDuration = (minutes) => {
  if (minutes < 60) {
    return `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
};

export const truncateText = (text, maxLength = 100) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substr(0, maxLength) + '...';
};

export const capitalizeFirst = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const getStatusBadgeClass = (status) => {
  const statusClasses = {
    'pending': 'bg-warning',
    'confirmed': 'bg-success',
    'shipped': 'bg-info',
    'delivered': 'bg-primary',
    'cancelled': 'bg-danger',
    'returned': 'bg-secondary',
    'live': 'bg-success',
    'scheduled': 'bg-warning',
    'ended': 'bg-secondary',
    'active': 'bg-success',
    'inactive': 'bg-secondary',
    'sold_out': 'bg-warning',
    'verified': 'bg-success',
    'pending_verification': 'bg-warning',
    'rejected': 'bg-danger'
  };
  
  return statusClasses[status] || 'bg-secondary';
};

export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

export const validateImageFile = async (file) => {
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
  const maxSize = 5 * 1024 * 1024; // 5MB
  
  if (!allowedTypes.includes(file.type)) {
    return 'Please select a valid image file (JPEG, PNG, or GIF)';
  }
  
  if (file.size > maxSize) {
    return 'Image size should be less than 5MB';
  }

  // Check dimensions for product images (500x500)
  try {
    const { ImageUtils } = await import('./imageUtils');
    const validation = await ImageUtils.validateProductImageDimensions(file);
    
    if (!validation.isValid) {
      return validation.message;
    }
  } catch (error) {
    console.error('Error validating image dimensions:', error);
    return 'Error validating image dimensions';
  }
  
  return null;
};

export const generateRandomId = () => {
  return Math.random().toString(36).substr(2, 9);
};

export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    console.error('Failed to copy text: ', err);
    return false;
  }
};

export const downloadFile = (url, filename) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const formatLivestreamDate = (dateString, fallbackText = 'Available today') => {
  // Handle null, undefined, or empty string
  if (!dateString || dateString === null || dateString === undefined || dateString === '') {
    return fallbackText;
  }
  
  const date = new Date(dateString);
  
  // Check if date is valid
  if (isNaN(date.getTime()) || date.getFullYear() < 1970) {
    return fallbackText;
  }
  
  return date.toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const hasActiveMediaTracks = () => {
  // Check if there are any active media tracks
  if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
    return navigator.mediaDevices.enumerateDevices()
      .then(devices => {
        // Check if any devices are currently in use
        const activeDevices = devices.filter(device => 
          device.kind === 'videoinput' || device.kind === 'audioinput'
        );
        return activeDevices.length > 0;
      })
      .catch(() => false);
  }
  return Promise.resolve(false);
};

export const stopAllActiveMediaTracks = () => {
  // Force stop all media tracks to ensure camera light turns off
  console.log('🔄 Force stopping all active media tracks');
  
  // First, try to stop tracks from all video elements on the page
  const videoElements = document.querySelectorAll('video');
  videoElements.forEach(video => {
    if (video.srcObject) {
      const stream = video.srcObject;
      if (stream && typeof stream.getTracks === 'function') {
        stream.getTracks().forEach(track => {
          track.stop();
          console.log(`🛑 Stopped ${track.kind} track from video element: ${track.label}`);
        });
        video.srcObject = null;
      }
    }
  });

  // Check for global stream references that might exist
  if (window.currentMediaStream && typeof window.currentMediaStream.getTracks === 'function') {
    window.currentMediaStream.getTracks().forEach(track => {
      track.stop();
      console.log(`🛑 Stopped ${track.kind} track from global stream: ${track.label}`);
    });
    window.currentMediaStream = null;
  }

  // Use MediaStreamTrack.getSources if available (Chrome-specific)
  if (navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia) {
    // Force release by trying to access and immediately stop any active camera
    const constraints = { video: true, audio: false };
    navigator.mediaDevices.getUserMedia(constraints)
      .then(stream => {
        // Immediately stop the stream we just created
        stream.getTracks().forEach(track => {
          track.stop();
          console.log(`🛑 Force released camera hardware: ${track.label}`);
        });
        console.log('✅ Camera hardware force released');
      })
      .catch(() => {
        console.log('ℹ️ No camera access needed or already released');
      });
  }

  console.log('✅ All media track cleanup completed');
};

export const forceReleaseCameraHardware = async () => {
  // This function specifically targets camera hardware release
  console.log('📷 Force releasing camera hardware');
  
  try {
    // First priority: Stop tracks from any video elements
    const videoElements = document.querySelectorAll('video');
    let tracksFound = false;
    
    videoElements.forEach(video => {
      if (video.srcObject) {
        const stream = video.srcObject;
        if (stream && typeof stream.getTracks === 'function') {
          const videoTracks = stream.getVideoTracks();
          videoTracks.forEach(track => {
            if (track.readyState === 'live') {
              track.stop();
              console.log(`📷 Released active camera: ${track.label}`);
              tracksFound = true;
            }
          });
          // Clear the video element
          video.srcObject = null;
          video.pause();
        }
      }
    });

    // If no active tracks found, try the fallback method
    if (!tracksFound) {
      try {
        // Only use this as a last resort - get minimal access and immediately release
        const tempStream = await navigator.mediaDevices.getUserMedia({ 
          video: { 
            width: { ideal: 320 }, 
            height: { ideal: 240 },
            frameRate: { ideal: 15 }
          } 
        });
        
        const videoTracks = tempStream.getVideoTracks();
        videoTracks.forEach(track => {
          track.stop();
          console.log(`📷 Force released camera via temp stream: ${track.label}`);
        });
        
        tracksFound = videoTracks.length > 0;
      } catch (tempError) {
        console.log('ℹ️ No camera access available for force release');
      }
    }
    
    // Small delay to ensure hardware release
    if (tracksFound) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    console.log('✅ Camera hardware release completed');
    return tracksFound;
  } catch (error) {
    console.log('ℹ️ Camera hardware release failed or not needed:', error.message);
    return false;
  }
};

export const forceReleaseCameraHardwareSync = () => {
  // Synchronous version for critical path scenarios (like navigation)
  console.log('📷 Force releasing camera hardware (sync)');
  
  try {
    // Immediately stop tracks from video elements - this is synchronous
    const videoElements = document.querySelectorAll('video');
    let releasedTracks = 0;
    
    videoElements.forEach(video => {
      if (video.srcObject) {
        const stream = video.srcObject;
        if (stream && typeof stream.getTracks === 'function') {
          stream.getTracks().forEach(track => {
            if (track.readyState === 'live') {
              track.stop();
              releasedTracks++;
              console.log(`📷 SYNC: Stopped ${track.kind} track: ${track.label}`);
            }
          });
          // Immediately clear video element
          video.srcObject = null;
          video.pause();
        }
      }
    });

    // Clear any global stream references
    if (window.currentMediaStream) {
      if (typeof window.currentMediaStream.getTracks === 'function') {
        window.currentMediaStream.getTracks().forEach(track => {
          if (track.readyState === 'live') {
            track.stop();
            releasedTracks++;
            console.log(`� SYNC: Stopped global ${track.kind} track: ${track.label}`);
          }
        });
      }
      window.currentMediaStream = null;
    }

    if (releasedTracks > 0) {
      console.log(`✅ SYNC: Released ${releasedTracks} active media tracks`);
    } else {
      console.log('ℹ️ SYNC: No active tracks found to release');
    }

    // As a fallback, try async cleanup but don't wait for it
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      navigator.mediaDevices.getUserMedia({ video: { width: 320, height: 240 } })
        .then(stream => {
          stream.getTracks().forEach(track => {
            track.stop();
            console.log(`📷 SYNC-ASYNC: Force stopped ${track.kind}: ${track.label}`);
          });
        })
        .catch(() => {
          console.log('ℹ️ SYNC-ASYNC: No additional cleanup needed');
        });
    }

  } catch (error) {
    console.error('❌ SYNC camera cleanup error:', error);
  }
};




